import { LEGEND_TYPES, WrapperWidgetUIProps } from '@carto/meridian-ds/widgets'
import { SourceFilters, SpatialFilter } from '@carto/react-redux'
import { QueryParameters, TilejsonResult } from '@deck.gl/carto'
import { Color } from '@deck.gl/core'
import { Position } from '@deck.gl-community/editable-layers'
import { z } from 'zod'
import { booleanValid } from '@turf/boolean-valid'

import { LayerGroup, RGBAColor, RGBColor } from '@carto/kepler.gl/reducers'
import { Layer, LayerBaseConfig } from '@carto/kepler.gl/layers'
import { AggregationType, CategoryResponse, RasterMetadata } from '@carto/api-client'

import { ProviderType } from 'features/connections/types'
import { MapType, Map, MapLockStatus, SqlParameter, MapDataset, Formats } from 'features/maps/types'
import { Client } from 'features/common/utils/clientParameter'
import { SchemaField } from 'features/data-explorer/types'
import {
  GeoJSONPolygonSchema,
  GeoJSONMultiPolygonSchema,
  LatitudeSchema,
  LongitudeSchema,
  KeplerAggregationTypeSchema
} from './schemas'

import {
  CartoMapLayer,
  CartoMapLayerConfig,
  ColorRange,
  ColorRangeMap,
  CustomMarkersRange,
  LayerType,
  RasterLayerConfigColorBand,
  RasterStyleType,
  VisualChannelField
} from 'features/maps/map-config/layer-types'
import { PopupField } from 'features/maps/map-config/popup-types'
import {
  BaseWidgetConfig,
  FormulaWidgetConfig,
  CategoryWidgetConfig,
  WidgetType,
  HistogramWidgetConfig,
  TimeSeriesWidgetConfig,
  TimeSeriesDataAndCategories,
  PieWidgetConfig,
  TableWidgetConfig,
  RangeWidgetConfig
} from 'features/maps/map-config/widget-types'
import { KeplerAggregationType, KeplerFieldType } from 'features/maps/map-config/kepler-types'

export { RasterStyleType, LayerTypes } from 'features/maps/map-config/layer-types'
export { MapRightPaneTab as RightPaneTab } from 'features/maps/map-config/map-config'
export { PopupEvent, PopupStyle } from 'features/maps/map-config/popup-types'
export { WidgetType, NoteFormat, TableColumnType } from 'features/maps/map-config/widget-types'
export { LegendSettingsVersion } from 'features/maps/map-config/legend-types'

export type { KeplerAggregationType, KeplerFieldType } from 'features/maps/map-config/kepler-types'
export type {
  LayerType,
  CustomMarkersRange,
  CustomMarkerEntry,
  ColorBand,
  RasterLayerConfigColorBand
} from 'features/maps/map-config/layer-types'
export type {
  PopupField,
  PopupFormatSettings,
  PopupSettings,
  LayerPopupSettings
} from 'features/maps/map-config/popup-types'
export type {
  FilterableWidget,
  TimeSeriesWidgetSerie,
  TimeSeriesDataAndCategories,
  TableColumn
} from 'features/maps/map-config/widget-types'
export type {
  LayerLegendSettings,
  LayerLegendEntrySettings,
  LegendHexColorMaps,
  LegendSettings,
  LegendHexColorMap
} from 'features/maps/map-config/legend-types'

export enum DistanceUnits {
  Kilometers = 'kilometers',
  Miles = 'miles'
}

export enum MinimizedDistanceUnits {
  Kilometers = 'km',
  Miles = 'mi',
  Meters = 'm',
  Feet = 'ft'
}

export enum BasemapProvider {
  CARTO = 'carto',
  Google = 'google',
  Amazon = 'amazon', // deprecated
  Custom = 'custom'
}

export enum MapStyleType {
  /**
   * Carto stock style (rendered by Maplibre)
   * NOTE: CARTO_MAPLIBRE would be more accurate
   */
  MAPLIBRE = 'mapLibre', // default, not required

  /**
   * Google Maps style (rendered by Google Maps API)
   */
  GOOGLE_MAPS = 'gmaps',

  /**
   * User supplied style (rendered by Maplibre)
   * NOTE: so CUSTOM_MAPLIBRE would be more accurate
   */
  CUSTOM = 'custom',

  /** @deprecated Kept for documentation of removed features */
  AMAZON = 'amazon'
}

export enum GoogleMapsMapTypeId {
  ROADMAP = 'roadmap',
  SATELLITE = 'satellite',
  HYBRID = 'hybrid',
  TERRAIN = 'terrain'
}

export type GoogleMapStyle = {
  type: MapStyleType.GOOGLE_MAPS
  options: {
    mapId?: string
    mapTypeId?: GoogleMapsMapTypeId
  }
}

export interface BuilderBasemapStyle {
  // inherited from KeplerMapStyle model
  type: MapStyleType
  id: string
  label: string
  icon?: string
  layerGroups?: LayerGroup[]
  url?: string

  // Actual style is react-map/malibre MapStyle | GoogleMapStyle, but it's impossible
  // to use it as it causes
  //    Type instantiation is excessively deep and possibly infinite
  // when trying to instantiate type for reducers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any // MapStyle | GoogleMapStyle

  // carto internal
  failedToLoad?: boolean
  disabled?: boolean
  customAttribution?: string
  originalUrl?: string
}

export enum BuilderMode {
  Public = 'Public',
  Viewer = 'Viewer',
  Editor = 'Editor'
}

export type KeplerLayerTextLabel = LayerBaseConfig['textLabel']['0'] // LayerTextLabel
export type RealKeplerTableField_ = NonNullable<KeplerLayerTextLabel['field']> // Field

export type KeplerTableField = RealKeplerTableField_ & {
  id: string
  /** Type as in Keplers ALL_FIELD_TYPES  */
  type: KeplerFieldType

  // Carto extensions
  selectedAggregator?: string
  artificial?: boolean

  // Only if hex color is used, column selected to get HEX colors.
  colorColumn?: string
  colorColumnIdx?: number
}

export enum GeometryType {
  Point = 'Point',
  MultiPoint = 'MultiPoint',
  Line = 'Line',
  LineString = 'LineString',
  MultiLineString = 'MultiLineString',
  Polygon = 'Polygon',
  MultiPolygon = 'MultiPolygon',
  Unknown = 'Unknown'
}

export enum KeplerScaleType {
  Linear = 'linear',
  Quantize = 'quantize',
  Quantile = 'quantile',
  Ordinal = 'ordinal',

  /**
   * Custom scale - requres `colormap` defined in related colorRange.
   *
   * In Builder, `Logarithmic scale` is _discrete_ and so implemented as `custom`
   * with `uiCustomScaleType` indicator set to `logarithmic` in `colorRange.
   */
  Custom = 'custom',
  Identity = 'identity'
}

export type SchemaColumn = {
  name: string
  type: string
}

export type KeplerTableMetadata = {
  tilejson?: TilejsonResult | null

  /**
   * Known values:
   *  * `tileset` for most _tile_ layers, even if it's `query` or `table`
   *  * `raster` for `raster` layers
   *  * `table` for deprecated document mode layers
   */
  type: MapType

  /** Same as `MapDataset.type` for tile layers. */
  sourceType?: MapType

  schema?: SchemaField[]

  /** Copied from `MapDataset.aggregationExp` */
  aggregationExp?: string | null

  hexColumnStats?: Record<string, AttributeStats>
  raster_metadata?: RasterMetadata
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any
}

export type KeplerTable = {
  id: string

  /// Color used to represent the dataset in the UI
  color: number[]

  metadata: KeplerTableMetadata
  fields: KeplerTableField[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allData: { [x: string]: any }[]
  allIndexes: number[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataContainer?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
  // Label derived (same as MapDataset.label)
  label: string

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gpuFilter?: any

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterRecord?: any
}

export type KeplerVisualChannel<T = unknown> = {
  key: string

  /** Name fo property in `visConfig` if `layer.config[field]` is empty. */
  property: string

  /** name of property in `layerConfig` containing selected field */
  field: string
  range: string
  domain: string
  scale: string
  channelScaleType: string
  nullValue: T

  /** Name of aggregationproperty in KeplerLayerVisConfig */
  aggregation?: string | null
  accessor?: string
  supportedFieldTypes?: string[]

  condition?: (layerConfig: CartoMapLayerConfig) => boolean

  getAttributeValue?: (config: KeplerLayerConfig) => T
  // defaultValue must be static as it's used in updateTriggers
  defaultValue?: T | ((layerConfig: KeplerLayerConfig) => T)

  // added by Carto
  opacityProperty?: string
}

export type KeplerLayerVisConfigSetting = {
  type: string /// known values: 'number', 'boolean', 'color-range-select', 'color-select'
  isRanged?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue: any // known values: 1, [1,2]
  range?: [number, number]
  step?: number
  property: string // name of property in KeplerLayerVisConfig
}

export type KeplerColorMapNumeric = [number | null, string][]
export type KeplerColorMapString = [string | null, string][]

export type KeplerColorMap = ColorRangeMap
export type KeplerColorRange = ColorRange

export type KeplerLayerVisConfig = {
  filled?: boolean
  colorAggregation?: KeplerAggregationType
  colorRange?: KeplerColorRange

  stroked?: boolean
  strokeColorAggregation?: KeplerAggregationType
  strokeColorRange?: KeplerColorRange

  sizeAggregation?: KeplerAggregationType
  heightAggregation?: KeplerAggregationType

  customMarkers?: boolean
  customMarkersUrl?: string
  customMarkersId?: string
  customMarkersRange?: CustomMarkersRange | null
  radius?: number
  radiusRange?: [number, number]

  enable3d?: boolean

  rasterStyleType?: RasterStyleType
  colorBands?: RasterLayerConfigColorBand[]
  uniqueValuesColorRange?: KeplerColorRange

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any
}

export type KeplerLayerConfig = {
  dataId: string
  label: string
  type: string
  isVisible: boolean
  isConfigActive: boolean
  highlightColor: RGBColor | RGBAColor
  hidden: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Record<string, any>
  color: number[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  animation?: any

  colorField?: KeplerTableField
  colorScale?: KeplerScaleType
  colorDomain?: Array<string | number | null>
  strokeColorField?: KeplerTableField
  sizeField?: KeplerTableField // this is used for lineWidgth / strokeWidth
  radiusField?: KeplerTableField
  customMarkersField?: KeplerTableField
  rotationField?: KeplerTableField
  heightField?: KeplerTableField
  weightField?: KeplerTableField

  uniqueValuesColorScale?: KeplerScaleType
  uniqueValuesColorField?: KeplerTableField
  uniqueValuesColorDomain: (number | null)[]

  textLabel: KeplerLayerTextLabel[]

  visConfig: KeplerLayerVisConfig
  visibilityByZoom?: {
    min: number
    max: number
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  colorUI: Record<string, any>

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any
}

export type KeplerLayerMeta = {
  bounds: [number, number, number, number]
  featureTypes?: { point?: boolean; polygon?: boolean; line?: boolean; linestring?: boolean }
  // Properties below are marked as 'required', to be stricter in most cases.
  // However, in 'document mode' (planned for removal) they may be undefined.
  formatTiles: string
  geoColumn: string
  scheme: string
  sourceType: MapType
  dataResolution?: number
  provider: ProviderType
  rasterMetadata?: RasterMetadata
}

// TODO(types): Consider renaming as CartoKeplerLayer or BuilderLayer.
// All layers with legends are KeplerLayers, once document mode is removed.
export type KeplerLayer = Layer & {
  id: string
  type: LayerType
  meta: KeplerLayerMeta
  readonly name: string
  readonly layerIcon: React.ComponentType

  readonly requireData: boolean
  readonly config: KeplerLayerConfig
  readonly visualChannels: Record<string, KeplerVisualChannel>
  readonly visConfigSettings: Record<string, KeplerLayerVisConfigSetting>
  readonly isLoadingTiles: boolean

  readonly canDropFeatures: boolean

  updateLayerConfig(newConfig: Partial<KeplerLayerConfig>): void
  validateScale(channelKey: string): void
  getScaleOptions(channelKey: string): string[]

  // Carto methods
  getNewLayerConfig?: (dataset: KeplerTable, layers: KeplerLayer[]) => Partial<KeplerLayerConfig>
  isColoredByColorColumn(rangeFieldName: string): boolean
  updateColorMap(rangeFieldName: string, colorMap: KeplerColorMap, colorRange: KeplerColorRange): KeplerColorRange
}

// Kepler serializes visual channels in different way that it keeps in state
export type KeplerStoredVisualChannel = VisualChannelField

export type FindDefaultLayerResult = {
  props: Partial<KeplerLayerConfig>[]
}

export type KeplerLayerClass = {
  new (props?: KeplerLayerConfig): KeplerLayer
  type: string

  findDefaultLayerProps(dataset: KeplerTable, previous): FindDefaultLayerResult
}

export type KeplerLayerClasses = Partial<Record<LayerType, KeplerLayerClass>>

// this is same as "serialized" KeplerLayer or `MapConfigLayer` from carto/api-client
// TODO: consider moving it somewhere when learning more about it

export type BuilderLayer = CartoMapLayer

// and this is builder layer with data loaded
export type BuilderLayerWithData = BuilderLayer & {
  mapDataset: MapDataset
  data?: TilejsonResult
}

export type BuilderDatasetInternalFields = {
  letter: string
  color: string // mapDataset has optional color, now it's always defined

  tilejson?: TilejsonResult | null
  schema?: SchemaField[]
  metadata?: KeplerTableMetadata
}

export type SqlParameterDataSource = {
  id: string
  name: string
  color: string
}
export type BoundSqlParameter = SqlParameter & {
  dataSources: SqlParameterDataSource[]
}

export type BuilderDataset = MapDataset & BuilderDatasetInternalFields
export type BuilderDatasetEnriched = BuilderDataset & KeplerTable

export type MapDatasetEnriched = MapDataset & KeplerTable

export interface CartoLayerOpts {
  datasetId?: string
  provider: ProviderType
  type: MapType
  source: string
  format?: Formats
  geoColumn?: string | null
  columns?: string[] | null
  connection?: string
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  queryParameters?: QueryParameters | null
  queryTemplate?: string | null
  filters?: SourceFilters
}

export interface LayerRequest extends CartoLayerOpts {
  mapId: string
  accessToken?: string
  fromAnalysis?: boolean
  minimized?: boolean
  sqlParameters?: SqlParameter[]
  createLayer?: boolean
}

export type LayerMove = 'front' | 'forward' | 'backward' | 'back'

export type InitBuilderRequestParams = {
  builderMode: BuilderMode
  mapId?: string
  createMapRequest?: CreateMapRequest
  searchParams?: Record<string, string>
}

export interface CreateMapRequest {
  map: Omit<Map, 'id'>
  layer?: CartoLayerOpts
}

export interface FetchLayerDataProps extends CartoLayerOpts {
  client?: Client
  headers?: Record<string, string>
  accessToken?: string
  expectErrorWithSchema?: boolean
  mapId: string
}

export interface MapCredentials {
  accessToken: string
  apiBaseUrl: string
}

export interface MeasureTooltip {
  position: Position
  distance: number
}

export const BUILDER_MEASURE_TOOL_UNIT_KEY = 'builder-measureToolUnit'
export const BUILDER_MOBILE_MODAL_DONOTSHOW_KEY = 'BUILDER_MOBILE_MODAL_DONOTSHOW_KEY'

export type WidgetState<T> =
  | {
      state: WidgetStateType.Loading
    }
  | {
      state: WidgetStateType.Success
      data: T
    }
  | {
      state: WidgetStateType.Error
      error?: string
      warning?: string
    }

export enum WidgetStateType {
  Loading = 'loading',
  Success = 'success',
  Error = 'error'
}

export enum BuilderWidgetPlacement {
  RightPane = 'rightPane',
  BottomPane = 'bottomPane'
}

export interface BaseWidget extends BaseWidgetConfig {
  isValid: boolean
  wrapperProps?: Partial<WrapperWidgetUIProps>
  state?: WidgetState<unknown>
}

export interface FormulaWidget extends BaseWidget, FormulaWidgetConfig {
  state?: WidgetState<number>
}

export interface CategoryWidget extends BaseWidget, CategoryWidgetConfig {
  state?: WidgetState<CategoryResponse>
}

export interface HistogramWidget extends BaseWidget, HistogramWidgetConfig {
  state?: WidgetState<{
    min: number
    max: number
    ticks: number[]
    values: number[]
  }>
}

export interface TimeSeriesWidget extends BaseWidget, TimeSeriesWidgetConfig {
  /** Height of the chart. Default: 22 x theme.spacingValue. */
  height?: string
  state?: WidgetState<TimeSeriesDataAndCategories>
}

export interface PieWidget extends BaseWidget, PieWidgetConfig {
  state?: WidgetState<CategoryResponse>
}

type RowGeometry =
  | {
      type: 'Point'
      coordinates: [number, number]
    }
  | {
      type: 'LineString'
      coordinates: [number, number][]
    }
  | {
      type: 'Polygon'
      coordinates: [number, number][][]
    }

export type TableWidgetRow = {
  geoid: number
  name: string
} & {
  [key: string]: RowGeometry | string | bigint | number
}

export type LayerExtent = {
  xmin: number
  ymin: number
  xmax: number
  ymax: number
}

export interface TableWidget extends BaseWidget, TableWidgetConfig {
  // Omit 'data'; Table Widget may contain Quadbin values (BigInt) that are not serializable by Redux.
  state?: WidgetState<unknown>
}

export interface RangeWidget extends BaseWidget, RangeWidgetConfig {
  state?: WidgetState<{ min: number; max: number; values: { min: number; max: number } }>
}

export type Widget =
  | CategoryWidget
  | FormulaWidget
  | HistogramWidget
  | RangeWidget
  | TimeSeriesWidget
  | TableWidget
  | PieWidget

export type WidgetsMapConfig = {
  widgets: Widget[]
  filters: Record<string, SourceFilters>
  spatialFilter: SpatialFilter | null
}

export enum MapViewerType {
  public,
  shared
}

export interface FormattedLabel {
  value: number
  label: string
}

interface SplitMap {
  layers: { [layerId: string]: boolean }
}

export type SplitMaps = SplitMap[]

export interface LegendWidgetProps {
  type: LEGEND_TYPES
  basedOn?: string
  basedOnLabelId?: string
  colors?: string[] | Color[]
  labels?: string[] | FormattedLabel[]
  isStrokeColor?: boolean
  isCustomMarkers?: boolean // TODO: maybe some proper enum?
  truncated?: number
  customMarkers?: string | string[]
  fallbackMarker?: string
  maskedMarkers?: boolean
}

export interface LegendEntry {
  widgetProps: LegendWidgetProps
  visualChannel: string
  hash?: string
}

export const DEFAULT_MAP_INDEX = 0
export const SPLIT_MAP_INDEX = 1
export type MapIndex = typeof DEFAULT_MAP_INDEX | typeof SPLIT_MAP_INDEX

export type NoSpatialIndexType = 'geom'
export const NoSpatialIndex: NoSpatialIndexType = 'geom'

export enum TilingScheme {
  Xyz = 'xyz',
  H3 = 'h3',
  Quadbin = 'quadbin'
}

export enum SpatialDataType {
  Geo = 'geo',
  H3 = 'h3',
  Quadbin = 'quadbin'
}

export enum SpatialIndex {
  H3 = 'h3',
  QUADBIN = 'quadbin'
}

export type SpatialSelectorType = SpatialIndex | NoSpatialIndexType

export const SpatialIndexColumn = Object.freeze({
  [SpatialIndex.H3]: ['h3', 'hex', 'h3id', 'hex_id', 'h3hex'],
  [SpatialIndex.QUADBIN]: ['quadbin']
})

export interface AttributeStatsRequest {
  datasetId: string
  attribute: string
  maxNumberOfCategories?: number
  noCache?: boolean
  cacheMaxAgeInMinutes?: number
}

export enum AttributeType {
  String = 'String',
  Number = 'Number',
  Timestamp = 'Timestamp',
  Boolean = 'Boolean'
}

export type AttributeStatsBase = {
  attribute: string
  type: AttributeType
}

export type AttributeStatsNumber = AttributeStatsBase & {
  type: AttributeType.Number
  min: number
  avg: number
  max: number
  sum: number
  quantiles: Record<number, number[]>
}

export type AttributeStatsTimestamp = AttributeStatsBase & {
  type: AttributeType.Timestamp
  min: string
  max: string
}

export interface AttributeStatsStringCategory {
  category: string
  frequency: number
}

export type AttributeStatsString = AttributeStatsBase & {
  type: AttributeType.String | AttributeType.Boolean
  categories: AttributeStatsStringCategory[]
}

/**
 * Result of getAttributeStats request to backend.
 */
export type AttributeStats = AttributeStatsString | AttributeStatsNumber | AttributeStatsTimestamp

export enum ShareDialogTabs {
  ShareSettings = 'ShareSettings',
  MapSettings = 'MapSettings'
}

export enum ColorPalette {
  Picker = 'picker',
  Defined = 'defined'
}

export type PopupValue = {
  url?: string
  asImg?: boolean
  displayValue: string | null
  displayDeltaValue: string | null
}

export type PopupRow = {
  field: PopupField
  value: PopupValue
}

export type FeatureDetails = {
  lngLat: number[]
  layerId: string
  rows: PopupRow[]
  mapViewId: number
}

export type BuilderLockState = MapLockStatus & {
  takingOver?: boolean
  unlockedAt?: number
}

export type TakeOverResponseState = {
  active: boolean
  accepted?: boolean
  timedOut?: boolean
  userId?: string
  email?: string
  requestedTime?: number
}

export enum DatasetItemAction {
  AddLayer = 'addLayer',
  StartAnalysis = 'analysis',
  OpenSql = 'openSql',
  Delete = 'delete',
  QueryThisTable = 'queryThisTable',
  Duplicate = 'duplicate',
  Rename = 'rename',
  RefreshSource = 'refresh-source',
  SwapDataSource = 'swapDataSource',
  ShowInWorkflows = 'showInWorkflows'
}

export type SplitMapPanelSide = 'top' | 'bottom' | 'left' | 'right'

export enum RightPaneType {
  Controls = 'controls',
  Description = 'description'
}

export interface SQLPanelProps {
  mapId: string
  // It's undefined if the panel is not still attached to a dataset
  datasetId?: string
  provider?: ProviderType
  connection?: string
  query?: string
  fromAnalysis?: boolean
  minimized?: boolean
  key?: string
}

export interface AISettings {
  enabled: boolean
  mapContext: string
  conversationStarters: string[]
  userGuideEnabled: boolean
  userGuide: string
  /** After migrating {@link AISettings} to {@link Agent}, migration must not run again. [sc-505727] */
  migratedAt?: string
}

export type BuilderAgentDatasetConfig = {
  id: string
  source: string
  label: string
  connectionName: string
  provider: ProviderType
  type: MapType
  schema: SchemaColumn[]
  geoColumn?: string
}

export type BuilderAgentMapConfig = {
  title: string
  description: string
  splitMap: boolean
  layerVisibilityBySplitMap?: {
    leftMap: Array<{ layerId: string; isVisible: boolean }>
    rightMap: Array<{ layerId: string; isVisible: boolean }>
  }
  /** @deprecated See [sc-513716]. */
  mapState?: {
    latitude: number
    longitude: number
    zoom: number
  }
  connections: Array<{ name: string; provider_id: ProviderType }>
  datasets: Array<BuilderAgentDatasetConfig>
  layers: Array<{
    id: string
    type: string
    label: string
    dataId: string
    isVisible: boolean
    legend?: Array<{
      property: string
      basedOn?: string
      colors?: string[]
      labels?: string[]
      icons?: string[]
    }>
  }>
  widgets: Array<{
    id: string
    label: string
    note?: string
    dataSource: string
    type: WidgetType
    column?: string
    operation?: AggregationType
    operationColumn?: string
    global: boolean
    viewport: boolean
  }>
  mapSettings?: {
    'Export PDF'?: boolean
    'Search Address'?: boolean
    'Change Basemap'?: boolean
    'Export CSV'?: boolean
    'Measure Distance'?: boolean
  }
  parameterControls?: SqlParameter[]
}

export type BuilderAgentThread = {
  /** ID (`response.conversationId` in AI API) is a stable and unique identifier for the thread. */
  id: string
  /**
   * Parent ID (`response.id` in AI API) identifies the current context of the conversation, and
   * is updated with each AI API response, or when the conversation is interrupted and reset to
   * a previous state.
   */
  parent: string
  /** ID of the AI Agent, associated with a particular LLM model and prompt version. */
  agentId: string
  /** Version of the AI Agent, overriding the default prompt for a given agentId. */
  agentVersion: string
  /** Associates 'create_source' actions with datasets. */
  datasets: BuilderAgentDataset[]
  /** Associates 'create_layer' actions with datasets. */
  layers: BuilderAgentLayer[]
}

export enum BuilderAgentAuthor {
  User = 'User',
  Bot = 'Bot',
  System = 'System'
}

export enum BuilderAgentResourceState {
  Loading = 'loading',
  Success = 'success',
  Error = 'error'
}

/**
 * Binding from a create_layer {@link BuilderAgentAction} to the resulting
 * layer ID, with possible loading or error states. Intended to help limit
 * the amount of AI-specific metadata added to stored layer definitions.
 */
export type BuilderAgentLayer = {
  actionId: string
  state: BuilderAgentResourceState
  layerId?: string
  error?: string
} & (
  | { state: BuilderAgentResourceState.Loading; layerId?: string; error?: undefined }
  | { state: BuilderAgentResourceState.Success; layerId: string; error?: undefined }
  | { state: BuilderAgentResourceState.Error; layerId?: string; error: string }
)

/**
 * Binding from a create_source {@link BuilderAgentAction} to the resulting
 * dataset ID, with possible loading or error states. Intended to help limit
 * the amount of AI-specific metadata added to stored dataset definitions.
 */
export type BuilderAgentDataset = {
  actionId: string
  state: BuilderAgentResourceState
  datasetId?: string
  error?: string
} & (
  | { state: BuilderAgentResourceState.Loading; datasetId?: string; error?: undefined }
  | { state: BuilderAgentResourceState.Success; datasetId: string; error?: undefined }
  | { state: BuilderAgentResourceState.Error; datasetId?: string; error: string }
)

export type BuilderAgentConversationItem =
  | {
      author: BuilderAgentAuthor.User
      message: string
      date: string
      locations?: Array<{ geometry: GeoJSON.Point; label: string; removed: boolean }>
    }
  | { author: BuilderAgentAuthor.Bot; type: 'delta'; message: string }
  | { author: BuilderAgentAuthor.Bot; type: 'response'; parent: string; message: string }
  | { author: BuilderAgentAuthor.Bot; type: 'action_frontend'; actions: BuilderAgentAction[] }
  | { author: BuilderAgentAuthor.Bot; type: 'action_backend'; action: BuilderAgentBackendActionTypes }
  | { author: BuilderAgentAuthor.Bot; type: 'error'; error: string; message?: string }
  | {
      author: BuilderAgentAuthor.Bot
      type: 'action_backend_finished'
      action: BuilderAgentBackendActionTypes
      output: string
      error?: string
    }
  | { author: BuilderAgentAuthor.System; message: BuilderAgentSystemMessage }

export type BuilderAgentConversation = BuilderAgentConversationItem[]

export type BuilderAgentResponse =
  | {
      type: 'delta'
      message: string
    }
  | {
      type: 'response'
      thread: string
      parent: string
      message: string
    }
  | {
      type: 'action_frontend'
      actions: BuilderAgentAction[]
    }
  | {
      type: 'action_backend'
      action: BuilderAgentBackendActionTypes
    }
  | {
      type: 'action_backend_finished'
      action: BuilderAgentBackendActionTypes
      output?: string
      error?: string
    }
  | {
      type: 'error'
      error: BuilderAgentSystemMessage
      message?: string
    }

export interface useBuilderAgentActionProps {
  onResult: (response: BuilderAgentActionResult) => void
  actionToExecute: BuilderAgentAction | undefined
}

export enum BuilderAgentSystemMessage {
  MaxThreadsError = 'max_builder_gen_ai_threads',
  MaxTokensError = 'max_builder_gen_ai_tokens',
  UnspecifiedError = 'unspecified'
}

// Actions available to Builder Agents that will be executed server-side. The
// Agent uses versioned action names (e.g. execute_query_v1_0_0) but for
// frontend purposes the differences do not matter.
export enum BuilderAgentBackendActionTypes {
  ExecuteQuery = 'execute_query',
  LdsGeocode = 'lds_geocode'
}

export enum BuilderAgentActionTypes {
  Focus = 'set_map_center_and_zoom',
  FocusLayer = 'set_map_center_and_zoom_to_layer',
  GetFormulaWidget = 'get_formula_widget',
  GetCategoryWidget = 'get_category_widget',
  GetHistogramWidget = 'get_histogram_widget',
  GetTimeSeriesWidget = 'get_time_series_widget',
  FilterCategoryWidget = 'filter_category_widget',
  FilterHistogramWidget = 'filter_histogram_widget',
  FilterRangeWidget = 'filter_range_widget',
  SetLayersVisibility = 'set_layers_visibility',
  GetMapCoordinates = 'get_map_coordinates',
  GetSpatialFilter = 'get_spatial_filter',
  SetSpatialFilter = 'set_spatial_filter',
  SetSqlParameterText = 'set_sql_parameter_text',
  SetSqlParameterDateRange = 'set_sql_parameter_date_range',
  SetSqlParameterNumeric = 'set_sql_parameter_numeric',
  SetSqlParameterNumericRange = 'set_sql_parameter_numeric_range',
  AddSource = 'add_source',
  RemoveSource = 'remove_source',
  AddLayer = 'add_layer',
  RemoveLayer = 'remove_layer',
  SetLayerStyle = 'set_layer_style',
  AddMarker = 'add_marker',
  AsyncWorkflowJobCheckResults = 'async_workflow_job_check_results',
  AddSourceFromWorkflows = 'add_source_from_workflows'
}

export interface BuilderAgentAction {
  action: BuilderAgentActionTypes
  params?: Record<string, unknown>
  id: string
  isCompleted?: boolean
}

export const FocusParamsSchema = z.object({
  center: z.tuple([LatitudeSchema, LongitudeSchema]),
  zoom: z.number().optional()
})

export interface BuilderAgentFocusAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.Focus
  params: z.infer<typeof FocusParamsSchema>
}

export const FocusLayerParamsSchema = z.object({
  layerId: z.string()
})

export interface BuilderAgentFocusLayerAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.FocusLayer
  params: z.infer<typeof FocusLayerParamsSchema>
}

export const GetFormulaWidgetParamsSchema = z.object({
  widgetId: z.string()
})

export interface BuilderAgentGetFormulaWidgetAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.GetFormulaWidget
  params: z.infer<typeof GetFormulaWidgetParamsSchema>
}

export const GetCategoryWidgetParamsSchema = z.object({
  widgetId: z.string()
})

export interface BuilderAgentGetCategoryWidgetAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.GetCategoryWidget
  params: z.infer<typeof GetCategoryWidgetParamsSchema>
}

export const GetHistogramWidgetParamsSchema = z.object({
  widgetId: z.string()
})

export interface BuilderAgentGetGetHistogramWidgetAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.GetHistogramWidget
  params: z.infer<typeof GetHistogramWidgetParamsSchema>
}

export const GetTimeSeriesParamsSchema = z.object({
  widgetId: z.string(),
  timeRange: z.tuple([z.number(), z.number()])
})

export interface BuilderAgentGetTimeSeriesAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.GetTimeSeriesWidget
  params: z.infer<typeof GetTimeSeriesParamsSchema>
}

export const FilterCategoryWidgetParamsSchema = z.object({
  widgetId: z.string(),
  categories: z.array(z.string())
})

export interface BuilderAgentFilterCategoryWidgetAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.FilterCategoryWidget
  params: z.infer<typeof FilterCategoryWidgetParamsSchema>
}

export const FilterHistogramWidgetParamsSchema = z.object({
  widgetId: z.string(),
  intervals: z.array(z.array(z.number()))
})

export interface BuilderAgentFilterHistogramWidgetAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.FilterHistogramWidget
  params: z.infer<typeof FilterHistogramWidgetParamsSchema>
}

export const FilterRangeWidgetParamsSchema = z.object({
  widgetId: z.string(),
  range: z.array(z.number())
})

export interface BuilderAgentFilterRangeWidgetAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.FilterRangeWidget
  params: z.infer<typeof FilterRangeWidgetParamsSchema>
}

export const SetLayersVisibilityParamsSchema = z.object({
  layers: z.array(
    z.object({
      layerId: z.string(),
      visibility: z.boolean(),
      mapIndex: z.union([z.literal(0), z.literal(1)]).optional()
    })
  )
})

export interface BuilderAgentSetLayersVisibilityAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetLayersVisibility
  params: z.infer<typeof SetLayersVisibilityParamsSchema>
}

export const SetSpatialFilterParamsSchema = z
  .object({
    geometry: z.discriminatedUnion('type', [GeoJSONPolygonSchema, GeoJSONMultiPolygonSchema]).optional().or(z.null())
  })
  .refine(
    (data) => {
      const feature = data.geometry ? { type: 'Feature' as const, geometry: data.geometry, properties: {} } : undefined
      return feature && booleanValid(feature)
    },
    { message: 'GeoJSON geometry given is invalid' }
  )

export interface BuilderAgentSetSpatialFilterAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetSpatialFilter
  params: z.infer<typeof SetSpatialFilterParamsSchema>
}

export const SetSqlParameterTextParamsSchema = z.object({
  parameterId: z.string(),
  values: z.array(z.string())
})

export interface BuilderAgentSetSqlParameterTextAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetSqlParameterText
  params: z.infer<typeof SetSqlParameterTextParamsSchema>
}

export const SetSqlParameterDateRangeParamsSchema = z.object({
  parameterId: z.string(),
  min: z.string().optional(),
  max: z.string().optional()
})

export interface BuilderAgentSetSqlParameterDateRangeAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetSqlParameterDateRange
  params: z.infer<typeof SetSqlParameterDateRangeParamsSchema>
}

export const SetSqlParameterNumericParamsSchema = z.object({
  parameterId: z.string(),
  value: z.number()
})

export interface BuilderAgentSetSqlParameterNumericAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetSqlParameterNumeric
  params: z.infer<typeof SetSqlParameterNumericParamsSchema>
}

export const SetSqlParameterNumericRangeParamsSchema = z.object({
  parameterId: z.string(),
  min: z.number().optional(),
  max: z.number().optional()
})

export interface BuilderAgentSetSqlParameterNumericRangeAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetSqlParameterNumericRange
  params: z.infer<typeof SetSqlParameterNumericRangeParamsSchema>
}

export const AddSourceParamsSchema = z.object({
  type: z.enum(MapType),
  source: z.string(),
  connection: z.string(),
  provider: z.enum(ProviderType),
  geoColumn: z.string().optional()
})

export interface BuilderAgentAddSourceAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.AddSource
  params: z.infer<typeof AddSourceParamsSchema>
}

export const AddSourceFromWorkflowsParamsSchema = z.object({
  type: z.literal(MapType.table),
  source: z.string(),
  connection: z.string(),
  provider: z.enum(ProviderType),
  geoColumn: z.string().optional()
})

export interface BuilderAgentAddSourceFromWorkflowsAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.AddSourceFromWorkflows
  params: z.infer<typeof AddSourceFromWorkflowsParamsSchema>
}

export const RemoveSourceParamsSchema = z.object({
  datasetId: z.string()
})

export interface BuilderAgentRemoveSourceAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.RemoveSource
  params: z.infer<typeof RemoveSourceParamsSchema>
}

export const AddLayerParamsSchema = z.object({
  datasetId: z.string(),
  label: z.string(),
  hoverColumns: z.array(z.string()).optional(),
  hoverColumnsAggregation: z.record(z.string(), KeplerAggregationTypeSchema).optional()
})

export interface BuilderAgentAddLayerAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.AddLayer
  params: z.infer<typeof AddLayerParamsSchema>
}

export const RemoveLayerParamsSchema = z.object({
  layerId: z.string()
})

export interface BuilderAgentRemoveLayerAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.RemoveLayer
  params: z.infer<typeof RemoveLayerParamsSchema>
}

export const SetLayerStyleParamsSchema = z.object({
  layerId: z.string(),
  opacity: z.number().optional(),
  fillColor: z.string().optional(),
  fillColorColumn: z.string().optional(),
  fillColorColumnAggregation: KeplerAggregationTypeSchema.optional(),
  fillColorPalette: z.array(z.string()).optional(),
  outlineWidth: z.number().optional(),
  outlineColor: z.string().optional(),
  radius: z.number().optional(),
  radiusColumn: z.string().optional()
})

export interface BuilderAgentSetLayerStyleAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.SetLayerStyle
  params: z.infer<typeof SetLayerStyleParamsSchema>
}

export const AddMarkerParamsSchema = z.object({
  markers: z.array(
    z.object({
      latitude: LatitudeSchema,
      longitude: LongitudeSchema,
      label: z.string().nonempty({ message: 'Label cannot be empty' })
    })
  )
})

export interface BuilderAgentAddMarkerAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.AddMarker
  params: z.infer<typeof AddMarkerParamsSchema>
}

export const BuilderAgentAsyncWorkflowJobCheckResultsActionJobsSchema = z.object({
  connectionName: z.string(),
  jobId: z.string(),
  toolName: z.string().optional()
})

export type BuilderAgentAsyncWorkflowJobCheckResultsActionJobsParams = z.infer<
  typeof BuilderAgentAsyncWorkflowJobCheckResultsActionJobsSchema
>

export const AsyncWorkflowJobCheckResultsParamsSchema = z.object({
  jobs: z.array(BuilderAgentAsyncWorkflowJobCheckResultsActionJobsSchema)
})

export interface BuilderAgentAsyncWorkflowJobCheckResultsAction extends BuilderAgentAction {
  action: BuilderAgentActionTypes.AsyncWorkflowJobCheckResults
  params: z.infer<typeof AsyncWorkflowJobCheckResultsParamsSchema>
}

export interface BuilderAgentActionResult {
  id: string
  result: unknown
}
